<template>
  <section class="hero is-hero-bar is-main-hero">
    <title-bar :title-stack="titleStack" />

    <div class="page-toolbar">
      <!-- Left: registry selector -->
      <div class="page-toolbar-left">
        <registries-selector
          :registries="registries"
          :currentRegistry="currentRegistry"
          @handleChangedRegister="handleChangedRegister"
        />
      </div>

      <!-- Right: actions -->
      <div class="page-toolbar-right">
        <b-button icon-left="file-plus" type="is-primary" size="is-small" @click="addRegistryEntry">
          Inregistrare noua
        </b-button>

        <reserved-numbers v-if="registryId != 0" :registryId="registryId" />

        <b-tooltip label="Reincarcare date" type="is-dark" position="is-bottom">
          <b-button icon-left="refresh" type="is-light" size="is-small" @click="reloadData" />
        </b-tooltip>

        <b-tooltip label="Imprimare" type="is-dark" position="is-bottom">
          <b-button icon-left="printer" type="is-light" size="is-small" v-if="entries" @click="print()" />
        </b-tooltip>

        <download-excel :data="entries" :fields="json_fields" name="Inregistrari.xls" worksheet="Inregistrari">
          <b-tooltip label="Export Excel" type="is-dark" position="is-bottom">
            <b-button icon-left="file-excel" type="is-light" size="is-small" v-if="entries" />
          </b-tooltip>
        </download-excel>

        <b-button
          :type="isFilterSelected ? 'is-warning' : 'is-info is-light'"
          :icon-left="isFiltersOpened ? 'chevron-double-up' : 'chevron-double-down'"
          size="is-small"
          @click="isFiltersOpened = !isFiltersOpened"
        >
          {{ isFilterSelected ? 'Filtre active' : 'Filtre' }}
          <span class="ml-1 has-text-weight-normal">— {{ totalCount }}</span>
        </b-button>
      </div>
    </div>

    <!-- Stats panel (collapsible) -->
    <div class="stats-panel" v-if="registryId != 0 && defaultChart.chartData">
      <div class="stats-panel-header" @click="statsOpen = !statsOpen">
        <div class="stats-summary">
          <span class="stats-summary-item">
            <span class="stats-dot" style="background:#00d1b2"></span>
            <b>{{ todayEntriesCount }}</b> astazi
          </span>
          <span class="stats-summary-item stats-summary-item--danger">
            <span class="stats-dot" style="background:rgb(255,99,132)"></span>
            <b>{{ numberOfEntriesDeadlinePassed }}</b> termen depasit
          </span>
          <span class="stats-summary-item">
            <span class="stats-dot" style="background:rgb(54,235,111)"></span>
            <b>{{ numberOfEntriesCanBeClosed }}</b> pot fi inchise
          </span>
          <span class="stats-summary-item">
            <span class="stats-dot" style="background:rgb(255,205,86)"></span>
            <b>{{ numberOfEntriesWithoutDeadline }}</b> fara termen
          </span>
          <span class="stats-total">
            <b>{{ numberOfActiveEntries }}</b> documente in lucru
          </span>
        </div>
        <b-icon :icon="statsOpen ? 'chevron-up' : 'chevron-down'" size="is-small" class="stats-toggle-icon" />
      </div>

      <transition name="stats-slide">
        <div v-if="statsOpen" class="stats-panel-body">
          <div class="stats-chart">
            <pie-chart
              ref="bigChart"
              chart-id="big-line-chart"
              :chart-data="defaultChart.chartData"
              :extra-options="defaultChart.extraOptions"
              :height="180"
              :width="180"
            />
          </div>
          <div class="stats-cards">
            <div class="stat-card stat-card--primary" @click="applyStatFilter('today')">
              <p class="stat-label">Astazi</p>
              <p class="stat-value" style="color:#00d1b2">{{ todayEntriesCount }}</p>
              <p class="stat-sub">Inregistrate astazi</p>
            </div>
            <div class="stat-card stat-card--danger" @click="applyStatFilter('deadlinePassed')">
              <p class="stat-label">Necesita raspuns</p>
              <p class="stat-value" style="color:rgb(255,99,132)">{{ numberOfEntriesDeadlinePassed }}</p>
              <p class="stat-sub">Termen depasit</p>
            </div>
            <div class="stat-card stat-card--success" @click="applyStatFilter('canBeClosed')">
              <p class="stat-label">Pot fi inchise</p>
              <p class="stat-value" style="color:rgb(54,235,111)">{{ numberOfEntriesCanBeClosed }}</p>
              <p class="stat-sub">Au raspuns deja</p>
            </div>
            <div class="stat-card stat-card--warning" @click="applyStatFilter('withoutDeadline')">
              <p class="stat-label">Fara termen</p>
              <p class="stat-value" style="color:rgb(255,205,86)">{{ numberOfEntriesWithoutDeadline }}</p>
              <p class="stat-sub">Fara data limita</p>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Filters -->
    <RegistriesFilter
      :propIsOpened="isFiltersOpened"
      :propIsSelected="isFilterSelected"
      @update-opened-value="updateFilterOpenedStatus"
      @update-selected-value="updateFilterSelectedStatus"
      @filterRegistryEntries="filterRegistryEntries"
    />

    <card-component
      class="has-table has-mobile-sort-spaced"
      title="Inregistrari"
      icon="file-multiple"
      :hasButtonSlot="true"
    >
      <template #button>
        <div class="card-header-icon">
          <b-button
            :icon-left="viewMode === 'table' ? 'calendar-month' : 'table'"
            size="is-small"
            type="is-info is-light"
            :loading="isViewLoading"
            :disabled="isViewLoading"
            @click="toggleViewMode"
          >
            {{ viewMode === "table" ? "Vedere calendar" : "Vedere tabel" }}
          </b-button>
        </div>
      </template>
      <b-loading :is-full-page="false" :active="isViewLoading" />
      <registry-entries-table
        v-if="viewMode === 'table' && !isViewLoading"
        @handleDeleteRegistryEntry="handleDeleteRegistryEntry"
        @page-change="onPageChange"
        @search="onSearch"
        :entries="entries"
        :maxEntry="maxEntry"
        :isLoading="isLoading"
        :total="totalCount"
        :current-page="currentPage"
      />
      <registry-entries-calendar
        v-else-if="viewMode === 'calendar' && !isViewLoading"
        :entries="entries"
      />
    </card-component>
  </section>
</template>

<script>
import * as chartConfig from "@/components/Charts/chart.config";
import RegistryEntries from "@/services/registryEntries.service";
import { notify } from "@/helpers/notify";
import RegistriesService from "@/services/registry.service";
import RegistryEntriesTable from "@/components/registry/registryEntries-table";
import RegistryEntriesCalendar from "@/components/registry/registryEntries-calendar";
import RegistriesSelector from "@/components/registry/registries-selector";
import RegistriesFilter from "@/components/registry/registries-filter";
import CardComponent from "@/components/CardComponent";
import TitleBar from "@/components/TitleBar";
//import HeroBar from "@/components/HeroBar";
import ReservedNumbers from "../../components/registry/reservedNumbers.vue";
import PieChart from "@/components/Charts/PieChart";
import * as Printjs from "print-js";
import moment from "moment";

const _ = require("lodash");

export default {
  name: "RegistryEntries",
  components: {
    //HeroBar,
    TitleBar,
    PieChart,
    CardComponent,
    RegistryEntriesTable,
    RegistryEntriesCalendar,
    RegistriesSelector,
    ReservedNumbers,
    RegistriesFilter,
  },
  data() {
    return {
      json_fields: {
        "Numar inregistrare intern": "internalRegistrationNumber",
        "Data inreg intern": {
          callback: (value) => {
            return `${moment(value.internalRegistrationDate).format(
              "DD/MM/YYYY"
            )}`;
          },
        },
        Continut: "description",
        Emitent: "issuer",
        Departament: "department",
        "Nr. Inreg. extern": "externalRegistrationNumber",
        "Data limita raspuns": {
          callback: (value) => {
            return `${moment(value.deadline).format("DD/MM/YYYY")}`;
          },
        },
        "Continut raspuns": "responseDescription",
        "Data raspuns": {
          callback: (value) => {
            return `${moment(value.responseDate).format("DD/MM/YYYY")}`;
          },
        },
        "Tip document": "type",
      },
      defaultChart: {
        chartData: null,
        extraOptions: chartConfig.chartOptionsMain,
      },
      isLoading: true,
      registryId: 0,
      registries: [],
      entries: [],
      currentPage: 1,
      totalCount: 0,
      pageSize: 100,
      isFiltersOpened: false,
      isFilterSelected: false,
      reservedRegistrationNumbers: [],
      maxEntry: 0,
      numberOfActiveEntries: 0,
      numberOfEntriesDeadlinePassed: 0,
      numberOfEntriesCanBeClosed: 0,
      numberOfEntriesWithoutDeadline: 0,
      numberOfEntriesDeadlineNotPassed: 0,
      todayEntriesCount: 0,
      viewMode: "table",
      isViewLoading: false,
      statsOpen: false,
      searchTerms: {},
      registryEntriesFilters: {
        dateInterval: null,
        fromDate: null,
        toDate: null,
        status: null,
        deadlineInterval: null,
        response: null,
        registryId: 0,
      },
    };
  },
  mounted() {},
  created() {
    this.setupRegistries().then(() => {
      this.getRegistryEntries().finally(() => {
        this.isLoading = false;
      });
      this.getRegistryStats(this.registryId);
    });
  },
  methods: {
    exportData(data) {},
    toggleViewMode() {
      this.isViewLoading = true;
      const nextMode = this.viewMode === "table" ? "calendar" : "table";
      this.$nextTick(() => {
        setTimeout(() => {
          this.viewMode = nextMode;
          this.$nextTick(() => {
            this.isViewLoading = false;
          });
        }, 50);
      });
    },
    print() {
      printJS({
        printable: this.entries,
        properties: [
          {
            field: "internalRegistrationNumber",
            displayName: "Nr. Inreg. intern",
          },
          { field: "department", displayName: "Department" },
          { field: "description", displayName: "Continut" },
        ],
        type: "json",
        documentTitle: "Inregistrari",
        showModal: true,
        modalMessage: "Generare raport",
        style: "td { text-align: center; }",
      });
    },
    getGreetingMessage() {
      var d = new Date();
      var time = d.getHours();
      if (time < 12) {
        return "Buna dimineata!";
      }
      if (time > 12) {
        return "Buna ziua!";
      }
    },
    fillChartData() {
      this.defaultChart.chartData = {
        datasets: [
          {
            label: "Statistici documente",
            data: [
              this.numberOfEntriesDeadlinePassed,
              this.numberOfEntriesCanBeClosed,
              this.numberOfEntriesWithoutDeadline,
            ],
            backgroundColor: [
              "rgb(255, 99, 132)",
              "rgb(54, 235, 111)",
              "rgb(255, 205, 86)",
            ],
            hoverOffset: 4,
          },
        ],
        labels: [
          "Necesita raspuns",
          "Contin deja un raspuns",
          "Nu au data limita de raspuns",
        ],
      };
    },
    reloadData() {
      this.isLoading = true;
      this.getRegistryEntries().finally(() => {
        this.isLoading = false;
      });
    },
    async handleChangedRegister(registryId) {
      this.registryId = registryId;
      this.currentPage = 1;
      this.isLoading = true;
      var retrievedObject = JSON.parse(localStorage.getItem("filters"));
      retrievedObject.registryId = this.registryId;
      localStorage.setItem("filters", JSON.stringify(retrievedObject));
      this.getRegistryEntries(retrievedObject).finally(() => {
        this.isLoading = false;
      });
      this.getRegistryStats(this.registryId);
    },
    async setupRegistries() {
      const { getRegistries } = RegistriesService;
      await getRegistries().then((response) => {
        const data = response.data;
        this.registries = Array.isArray(data) ? data : data.items;
        let permission = this.$store.state.permissions[0];
        const matchByPermission = this.registries.find((o) =>
          o.permission.includes(permission[0])
        );

        if (localStorage["filters"] != undefined) {
          let storageFilters = JSON.parse(localStorage.getItem("filters"));
          const storedExists = this.registries.find(
            (r) => r.id === storageFilters.registryId
          );
          if (storedExists) {
            this.registryId = storageFilters.registryId;
            return;
          }
        }

        const fallback = matchByPermission || this.registries[0];
        if (!fallback) return;
        this.registryId = fallback.id;
        this.registryEntriesFilters.registryId = this.registryId;
        localStorage.setItem(
          "filters",
          JSON.stringify(this.registryEntriesFilters)
        );
      });
    },
    async getRegistryEntries() {
      var filters = JSON.parse(localStorage.getItem("filters"));
      // Merge inline text search terms into the filters sent to the API
      if (this.searchTerms) {
        Object.assign(filters, this.searchTerms);
      }
      const { getRegistryEntries } = RegistriesService;
      await getRegistryEntries(filters, this.currentPage, this.pageSize).then((response) => {
        const data = response.data;
        if (Array.isArray(data)) {
          this.entries = data;
          this.totalCount = data.length;
        } else {
          this.entries = data.items;
          this.totalCount = data.totalCount;
        }
        this.maxEntry = this.entries.length > 0 ? this.entries[0].internalRegistrationNumber : 0;
      });
    },
    async onPageChange(page) {
      this.currentPage = page;
      this.isLoading = true;
      await this.getRegistryEntries();
      this.isLoading = false;
    },
    async onSearch(searchTerms) {
      this.searchTerms = searchTerms;
      this.currentPage = 1;
      this.isLoading = true;
      await this.getRegistryEntries();
      this.isLoading = false;
    },
    async getRegistryStats(registryId) {
      const { getRegistryStats, getRegistryEntries } = RegistriesService;
      const statsPromise = getRegistryStats(registryId).then((response) => {
        const { data } = response;
        this.numberOfActiveEntries = data.numberOfActiveEntries;
        this.numberOfEntriesDeadlinePassed = data.numberOfEntriesDeadlinePassed;
        this.numberOfEntriesCanBeClosed = data.numberOfEntriesCanBeClosed;
        this.numberOfEntriesWithoutDeadline =
          data.numberOfEntriesWithoutDeadline;
        this.numberOfEntriesDeadlineNotPassed =
          data.numberOfEntriesDeadlineNotPassed;
        this.fillChartData();
      });
      const todayPromise = getRegistryEntries({
        registryId: registryId,
        dateInterval: "today",
      }).then((response) => {
        this.todayEntriesCount = Array.isArray(response.data)
          ? response.data.length
          : 0;
      });
      await Promise.all([statsPromise, todayPromise]);
    },
    async addRegistryEntry() {
      this.$router.push({
        name: "registryEntry.add",
        params: { registryId: this.registryId },
      });
    },
    async filterRegistryEntries(filters) {
      this.currentPage = 1;
      var retrievedObject = JSON.parse(localStorage.getItem("filters"));
      retrievedObject.dateInterval = filters.dateInterval;
      retrievedObject.status = filters.status;
      retrievedObject.fromDate = filters.fromDate;
      retrievedObject.toDate = filters.toDate;
      retrievedObject.deadlineInterval = filters.deadlineInterval;
      retrievedObject.response = filters.response;
      localStorage.setItem("filters", JSON.stringify(retrievedObject));

      this.getRegistryEntries();
    },
    async handleDeleteRegistryEntry(id) {
      const { deleteRegistryEntry } = RegistryEntries;
      deleteRegistryEntry(id)
        .then((response) => {
          const { data, status } = response;
          if (status == "200") {
            const index = this.entries.findIndex((prop) => prop.id === id);
            this.entries.splice(index, 1);
            let maxEntry = this.entries[0].internalRegistrationNumber;
            this.maxEntry = maxEntry;
            notify.success(this, "Inregistrarea a fost stearsa.");
          } else {
            notify.error(this, "Inregistrarea nu a putut fi stearsa.");
          }
        })
        .catch(() => {
          notify.error(this);
        });
    },
    updateFilterOpenedStatus(val) {
      this.isFiltersOpened = val;
    },
    updateFilterSelectedStatus(val) {
      this.isFilterSelected = val;
    },
    applyStatFilter(type) {
      var filters = {
        dateInterval: null,
        fromDate: null,
        toDate: null,
        status: "În lucru",
        deadlineInterval: null,
        response: null,
      };

      if (type === "today") {
        filters.dateInterval = "today";
        filters.status = null;
      } else if (type === "deadlinePassed") {
        filters.deadlineInterval = "deadlinePassed";
      } else if (type === "canBeClosed") {
        filters.response = "withResponse";
      } else if (type === "withoutDeadline") {
        filters.deadlineInterval = "noDeadline";
      }

      this.isFilterSelected = true;
      this.isFiltersOpened = true;
      this.filterRegistryEntries(filters);
    },
  },
  computed: {
    titleStack() {
      return ["Registratura"];
    },
    heroRouterLinkTo() {
      return { name: "/announcements/add" };
    },
    currentRegistry() {
      return this.registries.find((item) => item.id === this.registryId);
    },
  },
};
</script>

<style scoped>
/* Toolbar */
.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  gap: 1rem;
  flex-wrap: wrap;
}

.page-toolbar-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.page-toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Stats panel */
.stats-panel {
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
}

.stats-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1.5rem;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.stats-panel-header:hover {
  background: #fafafa;
}

.stats-summary {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
  font-size: 0.875rem;
}

.stats-summary-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #4a4a4a;
}

.stats-summary-item--danger b {
  color: rgb(255, 99, 132);
}

.stats-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.stats-total {
  margin-left: 0.5rem;
  padding-left: 1rem;
  border-left: 1px solid #e0e0e0;
  color: #7a7a7a;
  font-size: 0.875rem;
}

.stats-toggle-icon {
  color: #aaa;
  flex-shrink: 0;
}

.stats-panel-body {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.5rem 1.25rem;
  border-top: 1px solid #f5f5f5;
}

.stats-chart {
  flex-shrink: 0;
}

.stats-cards {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  flex: 1;
}

.stat-card {
  flex: 1;
  min-width: 120px;
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 8px;
  padding: 0.85rem 1rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  text-align: center;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-card--primary { border-top: 3px solid #00d1b2; }
.stat-card--danger  { border-top: 3px solid rgb(255, 99, 132); }
.stat-card--success { border-top: 3px solid rgb(54, 235, 111); }
.stat-card--warning { border-top: 3px solid rgb(255, 205, 86); }

.stat-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9a9a9a;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.stat-sub {
  font-size: 0.75rem;
  color: #aaa;
}

/* Stats transition */
.stats-slide-enter-active,
.stats-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.stats-slide-enter,
.stats-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
